/**
 * Modelle für den Stammkunden-Bereich (Aufträge + Personen-Einsatzplanung).
 * Die String-Werte entsprechen den Backend-Enum-Namen (JsonStringEnumConverter).
 */

export type AuftragStatus =
  | 'New'
  | 'Planned'
  | 'InProgress'
  | 'Recorded'
  | 'Approved'
  | 'Completed'
  | 'Priced'
  | 'Billed';

/** Deutsche Anzeige-Labels für den Auftragsstatus. */
export const AUFTRAG_STATUS_LABELS: Record<AuftragStatus, string> = {
  New: 'Neu',
  Planned: 'Geplant',
  InProgress: 'In Arbeit',
  Recorded: 'Erfasst',
  Approved: 'Freigegeben',
  Completed: 'Abgeschlossen',
  Priced: 'Bepreist',
  Billed: 'Abgerechnet'
};

export const AUFTRAG_STATUS_ORDER: AuftragStatus[] = [
  'New', 'Planned', 'InProgress', 'Recorded', 'Approved', 'Completed', 'Priced', 'Billed'
];

export interface Auftrag {
  id: number;
  customerId: number;
  customerName?: string;
  title: string;
  description?: string;
  orderNumber?: string;
  receivedDate?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  status: AuftragStatus;
  location?: string;
  primaryWarehouseId?: number | null;
  primaryWarehouseName?: string | null;
  assignmentCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface AuftragCreate {
  customerId: number;
  title: string;
  description?: string;
  orderNumber?: string;
  receivedDate?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  location?: string;
  primaryWarehouseId?: number | null;
  status?: AuftragStatus;
}

/** Rolle eines Benutzers innerhalb eines Stammkunden. */
export type StammkundeRole = 'Bauleiter' | 'Vorarbeiter';

export interface StammkundeMember {
  userId: string;
  userName?: string | null;
  email?: string | null;
  role: StammkundeRole;
}

export interface MyStammkundeMembership {
  customerId: number;
  role: StammkundeRole;
}

export interface WarehouseRef {
  id: number;
  name: string;
}

export interface CustomerRef {
  id: number;
  name: string;
}

/** Vom Vorarbeiter selbst angelegter Spontan-Einsatz. */
export interface SpontanEinsatzCreate {
  customerId: number;
  auftragId?: number | null;
  title?: string;
  start: string;
  end: string;
  location?: string;
  notes?: string;
  participantUserIds?: string[];
  workerIds?: number[];
}

export interface PlannableUser {
  id: string;
  userName?: string | null;
  email?: string | null;
}

/** Arbeiter im Team eines Stammkunden – OHNE App-Login ("Personal"). */
export interface StammkundeWorker {
  id: number;
  name: string;
  phone?: string | null;
}

/** Status einer Materialposition (analog zu den Stunden). */
export type MaterialStatus = 'Submitted' | 'Approved' | 'Locked';

export const MATERIAL_STATUS_LABELS: Record<MaterialStatus, string> = {
  Submitted: 'Erfasst',
  Approved: 'Freigegeben',
  Locked: 'Abgerechnet'
};

/** Material eines Einsatzes (reine Dokumentation, ohne Preise). */
export interface EinsatzMaterial {
  id?: number | null;
  name: string;
  quantity: number;
  unit: string;
  status?: MaterialStatus;
}

/** Kennzahlen eines Projekts (Auftrag) für die Bauleiter-Gesamtübersicht. */
export interface AuftragOverview {
  auftragId: number;
  title: string;
  customerId: number;
  customerName?: string | null;
  status: AuftragStatus;
  totalHours: number;
  submittedHours: number;
  approvedHours: number;
  submittedHourCount: number;
  hourEntryCount: number;
  materialTotalCount: number;
  materialSubmittedCount: number;
  assignmentCount: number;
  lastActivityUtc?: string | null;
}

/** Stunden-Auswertung je Person (wer wie viel gearbeitet hat). */
export interface PersonHours {
  personName: string;
  workerId?: number | null;
  personUserId?: string | null;
  totalHours: number;
  submittedHours: number;
  approvedHours: number;
  entryCount: number;
}

/** Status eines Stunden-Eintrags. */
export type TimeEntryStatus = 'Submitted' | 'Approved' | 'Locked';

export const TIME_ENTRY_STATUS_LABELS: Record<TimeEntryStatus, string> = {
  Submitted: 'Erfasst',
  Approved: 'Freigegeben',
  Locked: 'Abgerechnet'
};

/** Erfasste Arbeitsstunden (ohne Preise). */
export interface ZeitEintrag {
  id: number;
  auftragId: number;
  assignmentId?: number | null;
  workDate: string;
  hours: number;
  personUserId?: string | null;
  workerId?: number | null;
  personName: string;
  note?: string | null;
  status: TimeEntryStatus;
  recordedByUserId: string;
  createdAtUtc: string;
}

export interface ZeitEintragCreate {
  assignmentId?: number | null;
  workDate: string;
  hours: number;
  personUserId?: string | null;
  workerId?: number | null;
  personName?: string | null;
  note?: string | null;
}

/** Einplanung (ResourceAssignment) im Auftrags-Kontext. */
export interface AuftragAssignment {
  id: number;
  assignedUserId: string;
  assignedUserName?: string | null;
  assignedUserEmail?: string | null;
  participantUserIds: string[];
  participantNames: string[];
  workerIds: number[];
  workerNames: string[];
  materials: EinsatzMaterial[];
  title: string;
  start: string;
  end: string;
  location?: string | null;
  customerId?: number | null;
  customerName?: string | null;
  auftragId?: number | null;
  auftragTitle?: string | null;
  notes?: string | null;
  workStatus: string;
  inviteSent: boolean;
  inviteSentAtUtc?: string | null;
}

export interface AuftragAssignmentCreate {
  assignedUserId: string;
  participantUserIds?: string[];
  workerIds?: number[];
  /** undefined = Material unverändert lassen; sonst Liste vollständig ersetzen. */
  materials?: EinsatzMaterial[];
  title?: string;
  start: string;
  end: string;
  location?: string;
  notes?: string;
  sendInvite?: boolean;
  workStatus?: string;
}
